import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { LoginService } from '@uoa/auth';
import { ExternalLoginService } from '../services/external-login.service';

/**
 * Redirects to our own /login page when there's no valid session, instead of
 * @uoa/auth's AuthGuard, which calls doLogin() and sends unauthenticated users
 * straight to the Cognito Hosted UI (UoA SSO) - skipping the choice between
 * UoA SSO and the external-user email/password form entirely.
 *
 * Pair with LoginSuccessGuard (which must run first) on routes that are also
 * the OAuth redirect_uri target, so a genuine login callback (?code=...) is
 * exchanged for tokens before this guard checks for them.
 */
@Injectable({
  providedIn: 'root'
})
export class RequireAuthGuard implements CanActivate {
  constructor(
    private loginService: LoginService,
    private externalLoginService: ExternalLoginService,
    private router: Router
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    if (await this.loginService.isAuthenticated()) {
      return true;
    }
    // isAuthenticated() only checks whether the 60-minute access token has expired - it
    // never tries the refresh token. Without this, users were sent to /login an hour after
    // logging in. 'unavailable' means a refresh token exists but Cognito can't be reached
    // (offline in the field) - let the user in on the existing session, like the original
    // app's long-lived token did; the next online refresh renews it.
    const result = await this.externalLoginService.refreshSession();
    if (result === 'refreshed' || result === 'unavailable') {
      return true;
    }
    return this.router.createUrlTree(['/login']);
  }
}
